import { useState } from "react";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import { useWindowInnerWidth } from "@hooks/useWindowInnerWidth";
import ArticleParagraph from "@routes/Article/components/ArticleParagraphElements/storyElement/ArticleParagraph";
import { ArticleStoryElementProps } from "@routes/Article/components/ArticleParagraphElements/utils/types";
import ArticleReadMore from "@routes/Article/components/ArticleReadMore";
import SixthParagraphWidget from "@routes/Article/components/SixthParagraphWidget/SixthParagraphWidget";
import { getIsBrandedContent } from "@routes/Article/utils/helpers";
import { getFormattedTextForAds } from "@routes/Section/utils/helpers";
import { checkIfFromPaidPressRelease, cn } from "@util/helpers";
import { useFeatureFlagEnabled } from "posthog-js/react";

type ParagraphWrapperProps = ArticleStoryElementProps;

/**
 * Renders a wrapper for paragraphs in an article, handling the display of ads, read more sections, and newsletter signups.
 * It manages the layout and visibility of various components based on the article's content and user status.
 */
const ParagraphWrapper = ({
  bodyString,
  article,
  adsWrapperClass,
  isBoxWrapped,
  index,
  gsChannels,
  displayReadMoreAndNewsletterSignupForm,
  className,
  paragraphIndices,
  articleIndex = 0,
  paragraphWidth,
  isFullContent,
  isPodcastAudioArticle = false,
}: ParagraphWrapperProps) => {
  const isUserLoginEnabled = useFeatureFlagEnabled("feature_enable_user_login");

  const sections = article?.sections || [];

  const isBrandedContent = getIsBrandedContent(article?.kicker || "");

  const isPaidPressRelease = checkIfFromPaidPressRelease(sections);
  const isDisplayAds =
    !isUserLoginEnabled || (!isBoxWrapped && !isPaidPressRelease);

  const windowInnerSize = useWindowInnerWidth();
  const [adsHeight, setAdsHeight] = useState(0);

  const relatedArticles = article?.relatedArticles || [];

  const noAdsDisplayed = adsHeight <= 5;

  const adsExtraCss = {
    maxWidth: `${windowInnerSize.width}px`,
    marginLeft: `calc(-1*(${windowInnerSize.width}px - 100%) / 2 )`,
    marginBottom: noAdsDisplayed ? "0" : "2rem",
    marginTop: noAdsDisplayed ? "0" : "2rem",
  };

  const mainSection = sections?.[0];
  const isHideAds = noAdsDisplayed;

  const btTags =
    article?.tags?.map(({ name }) => getFormattedTextForAds(name)).join(",") ||
    "";

  const totalParagraphs = paragraphIndices?.length || 0;

  return (
    <>
      <ArticleParagraph
        bodyString={bodyString}
        className={cn(className, paragraphWidth)}
      />

      {isFullContent &&
      [
        paragraphIndices?.[2],
        paragraphIndices?.[5],
        paragraphIndices?.[8],
      ].includes(index) ? (
        <Container rootClassName={cn("px-0 no-print", paragraphWidth)}>
          <>
            {paragraphIndices?.[2] === index && totalParagraphs !== 3 ? (
              <div
                className={cn("my-8 md:my-12", adsWrapperClass, {
                  "my-0 min-h-0 border-y-0 bg-none py-0": isHideAds,
                })}
                style={adsExtraCss}
              >
                <div className="no-print relative mx-auto w-full md:w-10/12 md:px-8 lg:w-8/12">
                  <Ad
                    adType="imu1"
                    setAdsHeight={setAdsHeight}
                    counter={articleIndex}
                    articleId={article?.id}
                    articleTags={btTags}
                    gsChannels={gsChannels}
                    className="flex justify-center"
                  />
                </div>
              </div>
            ) : null}

            {paragraphIndices?.[5] === index ? (
              <>
                {displayReadMoreAndNewsletterSignupForm &&
                totalParagraphs > 6 &&
                totalParagraphs <= 9 &&
                !isBrandedContent &&
                !isPodcastAudioArticle &&
                !isPaidPressRelease ? (
                  <>
                    <ArticleReadMore
                      readMoreArticle={relatedArticles}
                      linkParams={
                        new URLSearchParams({
                          ref: "article-see-also",
                        })
                      }
                    />
                  </>
                ) : null}

                {isDisplayAds &&
                totalParagraphs !== 6 &&
                mainSection &&
                !isPodcastAudioArticle ? (
                  <div
                    className={cn(adsWrapperClass, {
                      "min-h-0 border-y-0 bg-none py-0": isHideAds,
                    })}
                    style={adsExtraCss}
                  >
                    <div className="no-print relative mx-auto w-full md:w-10/12 md:px-8 lg:w-8/12">
                      <Ad
                        adType="midarticlespecial"
                        setAdsHeight={setAdsHeight}
                        counter={articleIndex}
                        articleId={article?.id}
                        articleTags={btTags}
                        gsChannels={gsChannels}
                        className="flex justify-center"
                      />
                    </div>
                  </div>
                ) : null}

                {totalParagraphs > 6 && !isBrandedContent ? (
                  <SixthParagraphWidget tags={article?.tags || []} />
                ) : null}
              </>
            ) : null}

            {paragraphIndices?.[8] === index && !isPodcastAudioArticle ? (
              <>
                {displayReadMoreAndNewsletterSignupForm &&
                totalParagraphs > 9 &&
                !isPaidPressRelease ? (
                  <ArticleReadMore
                    readMoreArticle={relatedArticles}
                    linkParams={
                      new URLSearchParams({
                        ref: "article-see-also",
                      })
                    }
                  />
                ) : null}

                {isDisplayAds && totalParagraphs !== 9 && mainSection ? (
                  <div
                    className={cn("my-8 md:my-12", adsWrapperClass, {
                      "min-h-0 border-y-0 bg-none py-0": isHideAds,
                    })}
                    style={adsExtraCss}
                  >
                    <div className="no-print mx-auto w-full md:w-10/12 md:px-8 lg:w-8/12">
                      <Ad
                        adType="imu2"
                        setAdsHeight={setAdsHeight}
                        counter={articleIndex}
                        articleId={article?.id}
                        articleTags={btTags}
                        gsChannels={gsChannels}
                        className="flex justify-center"
                      />
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        </Container>
      ) : null}
    </>
  );
};

export default ParagraphWrapper;
